"use client";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@workspace/ui/components/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { Button } from "@workspace/ui/components/button";
import { Badge } from "@workspace/ui/components/badge";
import { MoreHorizontal, FileText, Archive, Pencil } from "lucide-react";
import { useRouter } from "next/navigation";
import type { Document } from "@/lib/db/schema";

interface DocumentCardProps {
  document: Document & {
    role?: string;
    collections?: { id: string; name: string; icon: string | null }[];
  };
  onArchive?: (id: string) => void;
  onRename?: (id: string, title: string) => void;
}

export function DocumentCard({
  document,
  onArchive,
  onRename,
}: DocumentCardProps) {
  const router = useRouter();

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(hours / 24);

    if (hours < 1) return "Just now";
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  const handleRename = () => {
    const newTitle = window.prompt("Rename document:", document.title);
    if (newTitle && newTitle !== document.title) {
      onRename?.(document.id, newTitle);
    }
  };

  const collections = document.collections ?? [];

  return (
    <Card
      className="column-12 md:column-6 lg:column-4 cursor-pointer hover:border-primary/50 transition-colors group"
      onClick={() => router.push(`/${document.id}`)}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
            <CardTitle className="text-sm font-medium truncate">
              {document.title}
            </CardTitle>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem onClick={handleRename}>
                <Pencil className="h-4 w-4 mr-2" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => onArchive?.(document.id)}
                className="text-destructive"
              >
                <Archive className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-xs text-muted-foreground">
            Edited {formatDate(document.updatedAt)}
          </p>
          {document.role && document.role !== "owner" && (
            <Badge variant="secondary" className="text-[10px]">
              {document.role}
            </Badge>
          )}
        </div>
        {collections.length > 0 && (
          <div className="flex gap-1 mt-1.5 flex-wrap">
            {collections.map((c) => (
              <Badge
                key={c.id}
                variant="outline"
                className="text-[10px] px-1.5 py-0 h-4 font-normal"
              >
                {c.icon && <span className="mr-0.5">{c.icon}</span>}
                {c.name}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
